import { action, flow, makeObservable, observable, reaction, runInAction } from 'mobx';
import { agent } from 'src/api/agent';
import { BaseApiError, OperationAbortedError } from 'src/errors';

import { TPlanVersion } from '../comparison/types';
import { Directories } from '../directories/directories.store';
import { NotificationsStore } from '../notifications-store/notifications-store';

import { PadPreparation, RigSiteEvent } from './entities';
import { TRawPadPreparationWithRSE } from './types';
import { WellPadPreparationApi } from './well-pad-preparation.api';

interface IPlanVersionService {
  currentVersion: TPlanVersion | null;
  publicDraft: TPlanVersion | null;
  isDraftOfPublicDraftVersion(version: number | TPlanVersion): boolean;
}

export class WellPadPreparationService {
  private readonly api: WellPadPreparationApi;
  private readonly planVersion: IPlanVersionService;
  private readonly notificationsService: NotificationsStore;
  private readonly directoriesService: Directories;

  @observable private dataAbortController: AbortController | null = null;
  @observable private disposers: VoidFunction[] = [];

  @observable isDataFetching = false;
  @observable isInitialized = false;
  @observable.ref pads: PadPreparation[] = [];
  readonly padsMap = new Map<number, PadPreparation>();

  constructor(
    directoryService: Directories,
    planVersionsService: IPlanVersionService,
    notificationsService: NotificationsStore,
  ) {
    this.api = new WellPadPreparationApi(agent);
    this.notificationsService = notificationsService;
    this.directoriesService = directoryService;
    this.planVersion = planVersionsService;

    makeObservable(this);
  }

  @action
  private mapPadPreparations(rawPadPreparations: TRawPadPreparationWithRSE[]): void {
    const pads = [];

    for (const rawPadPreparation of rawPadPreparations) {
      const { rseList, ...rest } = rawPadPreparation;

      const newPad = new PadPreparation(rest);

      for (const rawRSE of rseList) {
        const rse = new RigSiteEvent(rawRSE);

        newPad.addRSE(rse);
      }

      pads.push(newPad);
      this.padsMap.set(newPad.id, newPad);
    }

    this.pads = pads;
  }

  @flow.bound
  private async *fetchPadPreparationByVersionId(versionId: number) {
    if (this.dataAbortController) {
      this.dataAbortController.abort();
    }

    const abortController = new AbortController();
    this.dataAbortController = abortController;

    this.isDataFetching = true;

    try {
      const data = await this.api.fetchPadPreparation(versionId, abortController.signal);
      yield;

      this.mapPadPreparations(data);
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return;
      }

      if (e instanceof BaseApiError && e.responseMessage) {
        this.notificationsService.showErrorMessage(e.responseMessage);
        return;
      }

      console.error(e);
    } finally {
      yield;
      this.isDataFetching = false;
      this.dataAbortController = null;
    }
  }

  @action.bound
  private async fetchPadPreparation() {
    const currentVersion = this.planVersion.currentVersion;
    const publicDraft = this.planVersion.publicDraft;

    if (!currentVersion || !publicDraft) {
      return;
    }

    if (currentVersion === publicDraft) {
      await this.fetchPadPreparationByVersionId(currentVersion.id);
      return;
    }

    if (this.planVersion.isDraftOfPublicDraftVersion(currentVersion)) {
      await this.fetchPadPreparationByVersionId(currentVersion.id);
    } else {
      await this.fetchPadPreparationByVersionId(publicDraft.id);
    }
  }

  @action.bound
  private initializeSubscriptions(): void {
    const disposers: VoidFunction[] = [...this.disposers];

    const planVersionDisposer = reaction(
      () => ({ currentVersion: this.planVersion.currentVersion, publicDraft: this.planVersion.publicDraft }),
      this.fetchPadPreparation,
    );

    disposers.push(planVersionDisposer);

    this.disposers = disposers;
    this.isInitialized = true;
  }

  @flow.bound
  private async *asyncInitialization() {
    await this.directoriesService.loadObjects(['Common_PadType']);
    await this.fetchPadPreparation();
    yield;

    this.initializeSubscriptions();
  }

  init = (): VoidFunction => {
    this.asyncInitialization();

    return () => {
      this.disposers.forEach((fn) => fn());
      this.dataAbortController?.abort();

      runInAction(() => {
        this.disposers = [];
        this.dataAbortController = null;
      });
    };
  };
}
